/**
 * Les gestes sur la version affichée d'une gamme, sous la rangée des versions :
 *
 * - un BROUILLON se publie (IMMUABLE ensuite), se corrige (date d'effet, source, motif) ou
 *   s'abandonne (archivé, jamais supprimé) ;
 * - une version PUBLIÉE se corrige en publiant une nouvelle version copiée d'elle, ou s'archive ;
 * - une version archivée ne se touche plus.
 *
 * Chaque geste irréversible annonce d'abord ce qu'il change (voir `domaine/gammes-versions.ts`).
 * Publier et archiver demandent le droit de publier ; créer et corriger un brouillon, celui de
 * gérer les gammes. L'API refuse de toute façon sans le droit.
 */
import { useState } from 'react';

import { api, type GammeDetail, type GammeVersion } from '../api/client';
import type { DroitsGammes } from '../domaine/droits';
import {
  aujourdhuiLocal,
  confirmationAbandon,
  confirmationArchivage,
  confirmationPublication,
  estEnVigueur,
  obstacleAPublication,
} from '../domaine/gammes-versions';
import { FormulaireVersion } from './FormulaireVersion';

export function ActionsVersion({
  detail,
  version,
  droits,
  enCours,
  agir,
  onCree,
  onModifiee,
  onConflit,
  onEditerContenu,
}: {
  detail: GammeDetail;
  version: GammeVersion;
  droits: DroitsGammes;
  enCours: boolean;
  /** Exécute le geste, annonce le résultat et relit la gamme ; garde cette version affichée. */
  agir: (action: () => Promise<unknown>, message: string, versionAGarder: string) => Promise<void>;
  onCree: (version: GammeVersion, depuis: GammeVersion) => void;
  onModifiee: (version: GammeVersion) => void;
  /** La version a été modifiée par quelqu'un d'autre : relire la gamme. */
  onConflit: (versionAGarder: string) => void;
  /** Ouvre l'éditeur du contenu du brouillon affiché. */
  onEditerContenu: () => void;
}) {
  const [panneau, setPanneau] = useState<'nouvelle' | 'infos' | null>(null);

  if (detail.archivedAt || version.statut === 'ARCHIVEE') return null;
  if (!droits.peutGerer && !droits.peutPublier) return null;

  const numero = version.versionNumber;

  const publier = () => {
    // Un brouillon vide ne se publie pas : le dire AVANT la confirmation « IMMUABLE ».
    const obstacle = obstacleAPublication(version);
    if (obstacle) {
      window.alert(obstacle);
      return;
    }
    if (!window.confirm(confirmationPublication(version, detail.enVigueur, aujourdhuiLocal()))) return;
    void agir(() => api.publishMaintenanceVersion(detail.id, version.id), `Version ${numero} publiée.`, version.id);
  };

  const archiver = () => {
    if (!window.confirm(confirmationArchivage(version, estEnVigueur(detail, version)))) return;
    void agir(() => api.archiveMaintenanceVersion(detail.id, version.id), `Version ${numero} archivée.`, version.id);
  };

  const abandonner = () => {
    if (!window.confirm(confirmationAbandon(version))) return;
    void agir(
      () => api.archiveMaintenanceVersion(detail.id, version.id),
      `Brouillon de la version ${numero} abandonné.`,
      version.id,
    );
  };

  return (
    <>
      <div className="saisie-ligne actions-version">
        {version.statut === 'BROUILLON' ? (
          <>
            {droits.peutGerer && (
              <button disabled={enCours} onClick={onEditerContenu}>
                Modifier le contenu
              </button>
            )}
            {droits.peutGerer && (
              <button className="secondaire" disabled={enCours} onClick={() => setPanneau('infos')}>
                Modifier les informations
              </button>
            )}
            {droits.peutPublier && (
              <button className="secondaire" disabled={enCours} onClick={publier}>
                Publier
              </button>
            )}
            {droits.peutPublier && (
              <button className="secondaire danger-discret" disabled={enCours} onClick={abandonner}>
                Abandonner ce brouillon
              </button>
            )}
          </>
        ) : (
          <>
            {droits.peutGerer && (
              <button className="secondaire" disabled={enCours} onClick={() => setPanneau('nouvelle')}>
                Nouvelle version à partir de celle-ci
              </button>
            )}
            {droits.peutPublier && (
              <button className="secondaire danger-discret" disabled={enCours} onClick={archiver}>
                Archiver cette version
              </button>
            )}
          </>
        )}
      </div>

      {panneau && (
        <FormulaireVersion
          mode={panneau}
          gamme={detail}
          version={version}
          onFermer={() => setPanneau(null)}
          onEnregistree={(v) => {
            setPanneau(null);
            if (panneau === 'nouvelle') onCree(v, version);
            else onModifiee(v);
          }}
          onConflit={() => {
            setPanneau(null);
            onConflit(version.id);
          }}
        />
      )}
    </>
  );
}
